// frontend/src/PayPalReturn.js
/**
 * PayPal Return Page
 * Handles user return after PayPal payment approval
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SERVER_URL } from './config';

const PayPalReturn = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const abortController = new AbortController();

    const capturePayment = async () => {
      const giftId = searchParams.get('gift_id');

      if (!giftId) {
        if (isMounted) {
          setStatus('error');
          setError('No gift ID found');
        }
        return;
      }

      try {
        const response = await fetch(
          `${SERVER_URL}/paypal/capture?gift_id=${giftId}`,
          {
            method: 'POST',
            credentials: 'include',
            signal: abortController.signal,
          }
        );

        if (!isMounted) return;

        const data = await response.json();

        if (!response.ok) {
          console.error('Capture failed:', data);
          throw new Error(data.detail || data.message || 'Failed to capture payment');
        }
        setStatus('success');

        // Redirect to success page after 2 seconds
        setTimeout(() => {
          if (isMounted) {
            navigate('/payment-confirmation', {
              state: {
                giftId: data.gift_id,
                amount: data.amount,
                fundingSource: data.funding_source,
                buyerEmail: data.buyer_email,
              },
            });
          }
        }, 2000);
      } catch (err) {
        if (err.name === 'AbortError') return; // Ignore aborted requests
        console.error('Payment capture error:', err);
        if (isMounted) {
          setStatus('error');
          setError(err.message);
        }
      }
    };

    capturePayment();

    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [searchParams, navigate]);

  return (
    <div className="container mt-5">
      <div className="row justify-content-center">
        <div className="col-md-6">
          {status === 'processing' && (
            <div className="card">
              <div className="card-body text-center p-5">
                <div className="spinner-border text-primary mb-3" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <h3>Processing your payment...</h3>
                <p className="text-muted">
                  Please wait while we confirm your PayPal payment.
                </p>
              </div>
            </div>
          )}

          {status === 'success' && (
            <div className="card">
              <div className="card-body text-center p-5">
                <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '4rem' }}></i>
                <h3 className="mt-3">Payment Successful!</h3>
                <p className="text-muted">
                  Your gift has been sent. Redirecting...
                </p>
              </div>
            </div>
          )}

          {status === 'error' && (
            <div className="card">
              <div className="card-body text-center p-5">
                <i className="bi bi-x-circle-fill text-danger" style={{ fontSize: '4rem' }}></i>
                <h3 className="mt-3">Payment Failed</h3>
                <p className="text-danger">{error}</p>
                <button
                  className="btn btn-primary mt-3"
                  onClick={() => navigate('/')}
                >
                  Return Home
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PayPalReturn;
